/**
 * unitCodeShorthand — resolving pasted shorthand to real unit codes
 * (Eric, 2026-10-27): '1re1tca' = 1RE001TCA, and '1re1' with no suffix = the unit
 * with model 1RE and number 001, whatever its suffix (TCA = California,
 * TIL = Illinois). Re-exported from unitCodeParser; Sales vendors this file too.
 */

import {
  afterOf,
  buildUnitCodeRegex,
  classifyCodeMatch,
  groupsOf,
  knownCodeSet,
  type UnitCodePartial,
} from './unitCodeMatch';

/**
 * A written code in its stored form: upper-cased, separators dropped, number padded
 * to 3 digits (`2re16til` → `2RE016TIL`, `1re-2-tca` → `1RE002TCA`). Anything that is
 * not code-shaped comes back upper-cased with separators dropped.
 */
export function normalizeUnitCode(code: string): string {
  const flat = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const match = /^([0-9A-Z]{3})(\d{1,4})([A-Z]{2,4})$/.exec(flat);
  return match ? match[1] + match[2].padStart(3, '0') + match[3] : flat;
}

export type UnitCodePartialResolution =
  | { status: 'unique'; code: string; candidates: string[] }
  | { status: 'ambiguous'; code: null; candidates: string[] }
  | { status: 'none'; code: null; candidates: string[] };

/**
 * Which of the caller's units a shorthand token (`1RE001`, or a `UnitCodePartial`)
 * names. Normally exactly one. `ambiguous` is the defensive path for legacy data
 * until create-side never-reuse enforcement lands (ISS-039): let the user pick.
 */
export function resolveUnitCodePartial(partial: UnitCodePartial | string, knownCodes: string[]): UnitCodePartialResolution {
  const token = (typeof partial === 'string' ? partial : partial.token).toUpperCase();
  const candidates = [
    ...new Set(
      knownCodes
        .map((c) => c.toUpperCase())
        .filter((c) => c.startsWith(token) && /^[A-Z]{2,4}$/.test(c.slice(token.length))),
    ),
  ].sort();
  if (candidates.length === 1) return { status: 'unique', code: candidates[0], candidates };
  if (candidates.length > 1) return { status: 'ambiguous', code: null, candidates };
  return { status: 'none', code: null, candidates };
}

export interface ExpandedUnitCodeText {
  /** The text with each shorthand that names exactly one unit replaced by its full code. */
  text: string;
  /** Shorthand that matches more than one unit: the user picks. */
  ambiguous: Array<{ token: string; candidates: string[] }>;
  /** Shorthand that matches no unit the caller can see. */
  missing: string[];
}

/**
 * Rewrite shorthand to full codes so line-by-line parsing (dates, site scoping)
 * works unchanged on the result. Everything else in the text is kept as written.
 */
export function expandUnitCodeShorthand(text: string, modelCodes: string[], knownCodes: string[]): ExpandedUnitCodeText {
  const ambiguous: Array<{ token: string; candidates: string[] }> = [];
  const missing: string[] = [];
  if (modelCodes.length === 0) return { text, ambiguous, missing };

  const known = knownCodeSet(knownCodes);
  const reported = new Set<string>();
  const expanded = text.replace(buildUnitCodeRegex(modelCodes), (...args: unknown[]) => {
    const whole = args[0] as string;
    const found = classifyCodeMatch(groupsOf(args), known, afterOf(args));
    if (found.kind !== 'partial') return whole;
    const resolution = resolveUnitCodePartial(found.partial, knownCodes);
    if (resolution.status === 'unique') return resolution.code + found.tail;
    if (!reported.has(found.partial.token)) {
      reported.add(found.partial.token);
      if (resolution.status === 'ambiguous') ambiguous.push({ token: found.partial.token, candidates: resolution.candidates });
      else missing.push(found.partial.token);
    }
    return whole;
  });

  return { text: expanded, ambiguous, missing };
}
